import '../form.css'

import { useMergeRefs } from '@floating-ui/react'
import { clsx } from 'clsx'
import type {
  ChangeEvent,
  FocusEvent,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
} from 'react'
import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Tooltip } from '#design-system/component/Tooltip/Tooltip'
import { Eye } from '#design-system/icons/eye'
import { EyeSlash } from '#design-system/icons/eye-slash'
import { X } from '#design-system/icons/x'
import type { LiteralsOnly } from '#design-system/types'

import type { FieldApiProps } from '../field'
import { forwardShellFocus, getMinWidthStyle, useDebouncedCommit } from '../field'
import { FieldFooter, resolveFooterMessage } from '../FieldFooter'
import { FieldLabel } from '../FieldLabel'

/** Input only handles single-line string-type inputs.
 * Each of the below types has a dedicated ARP component or is out of scope. */
type UnsupportedInputType =
  | 'button'
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'file'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'radio'
  | 'range'
  | 'reset'
  | 'submit'
  | 'time'
  | 'week'

/** The types Input accepts: "email" | "password" | "search" | "tel" | "text" | "url".
 * React's `HTMLInputTypeAttribute` includes a catch-all `string` member, which the exclusion
 * above can't see past, so `LiteralsOnly` strips it first. */
export type InputType = Exclude<LiteralsOnly<HTMLInputTypeAttribute>, UnsupportedInputType>

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'disabled' | 'prefix' | keyof FieldApiProps
>

interface BaseInputProps extends FieldApiProps, NativeInputProps {
  /** Controlled value. */
  value: string
  /** Committed value handler. Fires after debounce and on blur — not on every keystroke for perf.
   * Subscribe to `onLiveChange` when you genuinely need keystroke-level updates. */
  onChange: (value: string) => void
  /** Opt-in keystroke-level subscription. Fires on every change event, before debounce.
   * Most of the time, onChange is sufficient. Strive to do the minimal work needed in real-time
   * here and the rest in onChange. */
  onLiveChange?: (value: string) => void
  /** Inline content rendered before the input. Use for icons or short text (`$`, `@`). */
  prefix?: ReactNode
  ref?: Ref<HTMLInputElement>
}

/** A password field hides its value, so the suffix slot is reserved for the reveal toggle. */
interface PasswordProps {
  type: 'password'
  suffix?: never
}

/** Non-password types don't need a special override on suffix. */
interface PlainTextProps {
  /** HTML input type. Default 'text'. */
  type?: Exclude<InputType, 'password'>
  /** Inline content rendered after the input. Use for icons or short text (`%`, `.com`). */
  suffix?: ReactNode
}

export type InputProps = BaseInputProps & (PasswordProps | PlainTextProps)

/**
 * Single-line text input. Supports email / password / tel / url / search via the `type`
 * prop. Handles its own dirty state during typing and debounces parent commits so
 * keystroke-rate parent re-renders don't bottleneck dense forms.
 *
 * A search field with a value shows a clear button while hovered or focused.
 *
 * Error state derives from either the consumer-supplied `errorMessage` prop (always
 * visible) or the browser's HTML5 `validationMessage` for native attributes like
 * `required` / `pattern` / `type="email"` (becomes visible after first blur). Whichever
 * source fires, error styling and the display message update in sync.
 */
export const Input = (props: InputProps): ReactElement => {
  const {
    type = 'text',
    value,
    onChange,
    onLiveChange,
    label,
    helperText,
    errorMessage,
    disabledMessage,
    required,
    id,
    size = 'large',
    minChars,
    prefix,
    suffix,
    className,
    onBlur,
    onFocus,
    ref,
    ...rest
  } = props

  const { t } = useTranslation('design_system')

  const generatedId = useId()
  const fieldId = id ?? generatedId
  const footerId = `${fieldId}-footer`

  const [dirtyValue, setDirtyValue] = useState(value)
  const [lastSyncedValue, setLastSyncedValue] = useState(value)
  // A password field shows its value in plain text while the reveal toggle is on
  const [isValueRevealed, setIsValueRevealed] = useState(false)
  const [htmlValidationMessage, setHtmlValidationMessage] = useState('')
  const [hasBlurred, setHasBlurred] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  // Mirror the controlled value into local dirty state during render when the parent's
  // value changes. The double-state pattern (vs useEffect) avoids cascading renders —
  // React batches the two setState calls and re-runs render before painting.
  if (lastSyncedValue !== value) {
    setLastSyncedValue(value)
    setDirtyValue(value)
  }

  const { scheduleCommit, cancelPendingCommit } = useDebouncedCommit(onChange)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const setInputRef = useMergeRefs([inputRef, ref])

  const handleShellMouseDown = (event: MouseEvent<HTMLDivElement>): void => {
    forwardShellFocus(event, inputRef)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.value
    setDirtyValue(next)
    setHtmlValidationMessage(event.target.validationMessage)
    onLiveChange?.(next)
    scheduleCommit(next)
  }

  // Clearing commits right away rather than after the debounce
  const handleClear = (): void => {
    cancelPendingCommit()
    setDirtyValue('')
    onLiveChange?.('')
    onChange('')
    inputRef.current?.focus()
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true)
    onFocus?.(event)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    cancelPendingCommit()
    if (dirtyValue !== value) {
      onChange(dirtyValue)
    }
    setHtmlValidationMessage(event.target.validationMessage)
    setHasBlurred(true)
    setIsFocused(false)
    onBlur?.(event)
  }

  // HTML validity is gated to unfocused + at-least-once-blurred, so a fresh `required`
  // input doesn't yell on mount AND HTML messages don't flicker mid-typing (the parent's
  // errorMessage commits on debounce; surfacing HTML during the gap looks janky).
  // Consumer-supplied errorMessage is always visible and wins if both are present.
  const effectiveError =
    errorMessage ??
    (hasBlurred && !isFocused && htmlValidationMessage ? htmlValidationMessage : undefined)

  const hint = resolveFooterMessage({ errorMessage: effectiveError, helperText })

  const fieldStyle = minChars !== undefined ? { minWidth: getMinWidthStyle(minChars) } : undefined

  // The reveal toggle is intrinsic to a password field
  const isPassword = type === 'password'
  const resolvedSuffix = isPassword ? (
    <button
      aria-label={isValueRevealed ? t('form.hide_password') : t('form.show_password')}
      className="field-button"
      onClick={() => {
        setIsValueRevealed((revealed) => !revealed)
      }}
      type="button"
    >
      {isValueRevealed ? <EyeSlash /> : <Eye />}
    </button>
  ) : (
    suffix
  )

  const rootElement = (
    <div
      className={clsx(
        'doss-field',
        'doss-input',
        size,
        effectiveError && 'error',
        disabledMessage && 'disabled',
        className,
      )}
      style={fieldStyle}
    >
      {label !== undefined ? (
        <FieldLabel htmlFor={fieldId} required={required}>
          {label}
        </FieldLabel>
      ) : null}
      <div className="doss-input-shell" onMouseDown={handleShellMouseDown}>
        {prefix !== undefined && prefix !== null ? <span className="prefix">{prefix}</span> : null}
        <input
          ref={setInputRef}
          aria-describedby={hint ? footerId : undefined}
          aria-invalid={effectiveError ? true : undefined}
          disabled={Boolean(disabledMessage)}
          id={fieldId}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          required={required}
          type={isPassword && isValueRevealed ? 'text' : type}
          value={dirtyValue}
          {...rest}
        />
        {type === 'search' && dirtyValue !== '' && !disabledMessage ? (
          <button
            aria-label={t('form.clear')}
            className="clear field-button"
            onClick={handleClear}
            type="button"
          >
            <X />
          </button>
        ) : null}
        {resolvedSuffix !== undefined && resolvedSuffix !== null ? (
          <span className="suffix">{resolvedSuffix}</span>
        ) : null}
      </div>
      {hint ? (
        <FieldFooter id={footerId} kind={hint.kind}>
          {hint.text}
        </FieldFooter>
      ) : null}
    </div>
  )

  return disabledMessage !== undefined ? (
    <Tooltip title={disabledMessage}>{rootElement}</Tooltip>
  ) : (
    rootElement
  )
}
